import { useState } from 'react';
import { Badge, ErrorMessage, Field, Modal, Toggle, useToast } from '../../components/ui';
import { IconCheck, IconDownload, IconUpload } from '../../components/icons';
import { api, downloadFile } from '../../lib/api';

/**
 * Artikelimport aus Tabellen (Kap. 7).
 *
 * Drei Schritte: Datei wählen, Spalten zuordnen, buchen. Die Zuordnung wird
 * vorgeschlagen, aber nie erzwungen – Lieferantenkataloge beschriften ihre
 * Spalten, wie sie wollen, und ein Import, der stur „Artikelnummer“ verlangt,
 * scheitert am ersten „Art.-Nr.“.
 *
 * Zwischen Vorschau und Buchen liegt bewusst ein Klick: Erst zeigen, was
 * ankommt – 400 falsch zugeordnete Artikel sind mühsamer aufzuräumen als ein
 * zweiter Klick.
 */
export function ArticleImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { notify } = useToast();
  const [file, setFile] = useState<{ fileName: string; content: string } | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [updateExisting, setUpdateExisting] = useState(true);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const readFile = async (selected: File) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const buffer = await selected.arrayBuffer();
      // In Blöcken umwandeln: String.fromCharCode(...alles) sprengt bei großen
      // Dateien den Aufrufstapel.
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let offset = 0; offset < bytes.length; offset += 8192) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
      }
      const payload = { fileName: selected.name, content: btoa(binary) };
      const data = await api.post('/api/material/articles/import/preview', payload);
      setFile(payload);
      setPreview(data);
      setMapping(data.mapping ?? {});
    } catch (caught) {
      setError(caught);
      setFile(null);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const data = await api.post('/api/material/articles/import', { ...file, mapping, updateExisting });
      setResult(data);
      onImported();
      notify(`${data.created} Artikel angelegt, ${data.updated} aktualisiert.`, 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const fields: { key: string; label: string; required: boolean }[] = preview?.fields ?? [];
  const ready = mapping.articleNo !== undefined && mapping.name !== undefined;
  const existing = new Set<string>((preview?.existingArticleNos ?? []).map((entry: string) => entry.toLowerCase()));

  /** Wie viele Zeilen der Vorschau treffen auf einen vorhandenen Artikel? */
  const known =
    preview && mapping.articleNo !== undefined
      ? preview.sample.filter((row: string[]) => existing.has((row[mapping.articleNo] ?? '').trim().toLowerCase())).length
      : 0;

  return (
    <Modal
      title="Artikel importieren"
      size="xwide"
      onClose={onClose}
      footer={
        <>
          <span className="small subtle" style={{ marginRight: 'auto' }}>
            {preview ? `${preview.rowCount} Datenzeile(n) erkannt` : 'CSV, TXT oder XLSX – die erste Zeile enthält die Spaltenüberschriften.'}
          </span>
          <button className="btn" onClick={onClose}>
            {result ? 'Schließen' : 'Abbrechen'}
          </button>
          {!result && (
            <button className="btn primary" onClick={commit} disabled={!ready || busy}>
              {busy ? 'Importiere …' : `${preview?.rowCount ?? 0} Zeile(n) importieren`}
            </button>
          )}
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        {result ? (
          <div className="stack">
            <div className="alert success">
              <IconCheck width={16} height={16} />
              <span>
                Import abgeschlossen: <strong>{result.created}</strong> neu angelegt, <strong>{result.updated}</strong>{' '}
                aktualisiert, <strong>{result.skipped}</strong> übersprungen.
              </span>
            </div>
            {result.errors?.length > 0 && (
              <div className="stack" style={{ gap: 4 }}>
                <span className="section-title">Übersprungene Zeilen</span>
                {result.errors.map((entry: any) => (
                  <span key={entry.row} className="small subtle">
                    Zeile {entry.row}: {entry.message}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <Field
              label="Datei"
              required
              hint="Excel-Arbeitsmappen werden aus dem ersten Tabellenblatt gelesen; Formeln nicht ausgewertet."
            >
              <label className="btn" style={{ display: 'inline-flex', cursor: 'pointer' }}>
                <IconUpload width={14} height={14} />
                {file ? file.fileName : 'Datei auswählen …'}
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,text/csv,text/plain"
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    const selected = event.target.files?.[0];
                    if (selected) void readFile(selected);
                  }}
                />
              </label>
            </Field>

            <div className="note-box tone-info">
              <span>
                Unsicher, wie die Datei aussehen muss? Die Musterdatei enthält alle erkannten Spalten mit zwei
                Beispielzeilen und lässt sich unverändert wieder importieren.
              </span>
              <button
                className="btn sm"
                onClick={() =>
                  downloadFile('/api/material/articles/import/muster.csv', 'artikel-musterdatei.csv').catch(() =>
                    setError(new Error('Die Musterdatei konnte nicht geladen werden.')),
                  )
                }
              >
                <IconDownload width={14} height={14} /> Musterdatei
              </button>
            </div>

            {preview && (
              <>
                <span className="section-title">Spalten zuordnen</span>
                <div className="form-grid">
                  {fields.map((field) => (
                    <Field key={field.key} label={field.label} required={field.required}>
                      <select
                        value={mapping[field.key] ?? ''}
                        onChange={(event) =>
                          setMapping((current) => {
                            const next = { ...current };
                            if (event.target.value === '') delete next[field.key];
                            else next[field.key] = Number(event.target.value);
                            return next;
                          })
                        }
                      >
                        <option value="">– nicht übernehmen –</option>
                        {preview.headers.map((header: string, index: number) => (
                          <option key={index} value={index}>
                            {header || `Spalte ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>

                {!ready && (
                  <div className="alert warning">
                    Artikelnummer und Bezeichnung müssen einer Spalte zugeordnet sein.
                  </div>
                )}

                <Toggle
                  checked={updateExisting}
                  onChange={setUpdateExisting}
                  label={
                    <span>
                      Vorhandene Artikelnummern aktualisieren
                      <span className="small subtle" style={{ display: 'block' }}>
                        Ausgeschaltet werden bekannte Nummern übersprungen – es entstehen nur neue Artikel.
                      </span>
                    </span>
                  }
                />

                <div className="row between">
                  <span className="section-title">Vorschau</span>
                  {mapping.articleNo !== undefined && (
                    <span className="small subtle">
                      {known} der ersten {preview.sample.length} Zeilen treffen auf vorhandene Artikel
                    </span>
                  )}
                </div>

                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        {fields
                          .filter((field) => mapping[field.key] !== undefined)
                          .map((field) => (
                            <th key={field.key}>{field.label}</th>
                          ))}
                        <th style={{ width: 90 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.map((row: string[], index: number) => {
                        const articleNo = (row[mapping.articleNo] ?? '').trim();
                        const isKnown = existing.has(articleNo.toLowerCase());
                        return (
                          <tr key={index}>
                            <td className="small subtle">{index + 2}</td>
                            {fields
                              .filter((field) => mapping[field.key] !== undefined)
                              .map((field) => (
                                <td key={field.key} className="small">
                                  {row[mapping[field.key]] ?? ''}
                                </td>
                              ))}
                            <td>
                              {!articleNo ? (
                                <Badge variant="danger">fehlt</Badge>
                              ) : isKnown ? (
                                <Badge variant={updateExisting ? 'warning' : 'default'}>
                                  {updateExisting ? 'ändert' : 'übersprungen'}
                                </Badge>
                              ) : (
                                <Badge variant="success">neu</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {preview.rowCount > preview.sample.length && (
                  <span className="small subtle">
                    Gezeigt werden die ersten {preview.sample.length} Zeilen. Importiert werden alle{' '}
                    {preview.rowCount}.
                  </span>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
